import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { HOLIDAY_QUERY_KEYS } from './keys';

import * as holidayService from 'services/holidays';

import { handleError } from 'utils/errorHandler';

import type { Holiday } from 'interface/holiday';
import type { ServiceId, ServiceParams } from 'interface/service';

type HolidayListResponse = { data?: Holiday[]; meta?: { page?: number; total?: number } };

export const getHolidaysQueryKey = (params: ServiceParams = {}) => [HOLIDAY_QUERY_KEYS.LIST, params] as const;

export const holidaysQueryOptions = (params: ServiceParams = {}) => ({
  queryKey: getHolidaysQueryKey(params),
  queryFn: () => holidayService.fetch(params) as Promise<HolidayListResponse>,
});

export const useFetchHolidaysQuery = (
  params: ServiceParams = {},
  enabled = true
): UseQueryResult<HolidayListResponse, Error> => {
  return useQuery({
    ...holidaysQueryOptions(params),
    enabled,
  });
};

export const getHolidayByIdQueryKey = (id?: ServiceId) => [HOLIDAY_QUERY_KEYS.DETAIL, id] as const;

export const useFetchHolidayByIdQuery = <TData = Holiday>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getHolidayByIdQueryKey(id),
    queryFn: () => holidayService.fetchById(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

const useInvalidateHolidays = () => {
  const queryClient = useQueryClient();

  return {
    invalidateList: () => queryClient.invalidateQueries({ queryKey: [HOLIDAY_QUERY_KEYS.LIST] }),
    invalidateDetail: (id: ServiceId) => queryClient.invalidateQueries({ queryKey: getHolidayByIdQueryKey(id) }),
  };
};

const handleMutationError = (error: object) => handleError(error);

export const useCreateHolidayMutation = () => {
  const { invalidateList } = useInvalidateHolidays();

  return useMutation({
    mutationFn: (holiday: Holiday) => holidayService.create(holiday),
    onSuccess: invalidateList,
    onError: handleMutationError,
  });
};

export const useUpdateHolidayMutation = () => {
  const { invalidateList, invalidateDetail } = useInvalidateHolidays();

  return useMutation({
    mutationFn: ({ id, holiday }: { id: ServiceId; holiday: Holiday }) => holidayService.update(id, holiday),
    onSuccess: (_data, { id }) => {
      invalidateList();
      invalidateDetail(id);
    },
    onError: handleMutationError,
  });
};

export const useDeleteHolidayMutation = () => {
  const { invalidateList } = useInvalidateHolidays();

  return useMutation({
    mutationFn: (id: ServiceId) => holidayService.remove(id),
    onSuccess: invalidateList,
    onError: handleMutationError,
  });
};
